import {
  addSequence,
  deleteSequence,
  formatDuration,
  renameSequence,
  sequenceDuration,
  framesToSeconds,
  type TransitionType,
} from '@ve/core';
import { FolderPlus, Layers, Palette, Plus, Trash2, Type } from 'lucide-react';
import { useState, type DragEvent } from 'react';
import { addSolidClip, addTextClip } from '../../lib/commands.ts';
import { activeDrag, clearActiveDrag, DND_SEQUENCE, DND_TRANSITION } from '../../lib/dnd.ts';
import { edit, useEditor } from '../../state/editor-store.ts';
import { importMedia, pickAndImport, sourcesFromDrop } from '../../state/session.ts';
import { MediaFolderBar } from './MediaFolderBar.tsx';
import { MediaTab, newFolder, useCurrentFolder } from './MediaTab.tsx';
import { TRANSITION_DESCRIPTIONS, TRANSITION_LABELS } from '../../lib/labels.ts';
import { commandTip, tip } from '../../lib/tooltip.ts';

type Tab = 'media' | 'chapters' | 'effects';

export function LibraryPanel() {
  const [tab, setTab] = useState<Tab>('media');
  const [dropping, setDropping] = useState(false);
  const folderId = useCurrentFolder();

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDropping(false);
    const sources = sourcesFromDrop(e.dataTransfer); // must run synchronously in the event
    if (sources) void sources.then((list) => importMedia(list.map((source) => ({ source }))));
  };

  return (
    <section
      className={`panel library${dropping ? ' dropping' : ''}`}
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes('Files')) {
          e.preventDefault();
          setDropping(true);
        }
      }}
      onDragLeave={(e) => !e.currentTarget.contains(e.relatedTarget as Node | null) && setDropping(false)}
      onDropCapture={() => setDropping(false)} // also when a folder inside handles the drop
      onDrop={onDrop}
    >
      <div className="panel-header">
        <div className="tabs">
          <button
            className={`tab${tab === 'media' ? ' active' : ''}`}
            onClick={() => setTab('media')}
            {...tip('Imported videos, images and audio')}
          >
            Media
          </button>
          <button
            className={`tab${tab === 'chapters' ? ' active' : ''}`}
            onClick={() => setTab('chapters')}
            {...tip('Chapters: independent timelines you can nest into the main one')}
          >
            Chapters
          </button>
          <button
            className={`tab${tab === 'effects' ? ' active' : ''}`}
            onClick={() => setTab('effects')}
            {...tip('Text, color solids and transitions')}
          >
            Effects
          </button>
        </div>
        <span className="spacer" />
        {tab === 'media' && (
          <>
            <button
              className="btn small icon ghost"
              {...tip('New folder — organise media by dragging it onto a folder')}
              onClick={() => newFolder(folderId)}
            >
              <FolderPlus size={14} />
            </button>
            <button
              className="btn small icon"
              {...tip('Import video, image or audio files into this folder (or drop files and folders here)')}
              onClick={() => void pickAndImport()}
            >
              <Plus size={14} />
            </button>
          </>
        )}
      </div>
      <div className="panel-body">
        {tab === 'media' && (
          <>
            <MediaFolderBar />
            <MediaTab onImport={() => void pickAndImport()} />
          </>
        )}
        {tab === 'chapters' && <ChaptersTab />}
        {tab === 'effects' && <EffectsTab />}
      </div>
    </section>
  );
}

function ChaptersTab() {
  const project = useEditor((s) => s.project)!;
  const activeId = useEditor((s) => s.activeSequenceId);
  const openSequence = useEditor((s) => s.openSequence);
  const fps = project.settings.fps;
  const sequences = Object.values(project.sequences).sort((a, b) =>
    a.id === project.rootSequenceId ? -1 : b.id === project.rootSequenceId ? 1 : a.name.localeCompare(b.name),
  );
  return (
    <div className="chapter-list">
      <div className="chapter-help muted">
        Chapters are independent timelines. Drag one onto a track to nest it, or select clips and press{' '}
        <span className="kbd">⌘G</span> to group them into a new chapter.
      </div>
      {sequences.map((seq) => {
        const isRoot = seq.id === project.rootSequenceId;
        return (
          <div
            key={seq.id}
            className={`chapter-item${seq.id === activeId ? ' active' : ''}`}
            draggable={!isRoot}
            onDragStart={(e) => {
              e.dataTransfer.setData(DND_SEQUENCE, seq.id);
              activeDrag.sequenceId = seq.id;
            }}
            onDragEnd={clearActiveDrag}
            onDoubleClick={() => openSequence(seq.id)}
            {...tip(
              isRoot
                ? 'Main timeline — double-click to open'
                : `Double-click to open “${seq.name}”, or drag it onto a video track to nest it`,
            )}
          >
            <Layers size={14} />
            <div className="chapter-name">
              <div>
                {seq.name}
                {isRoot && <span className="muted"> · main</span>}
              </div>
              <div className="muted">
                {formatDuration(framesToSeconds(sequenceDuration(project, seq), fps))}
              </div>
            </div>
            <button
              className="btn ghost small"
              {...tip(`Rename “${seq.name}”`)}
              onClick={() => {
                const name = prompt('Rename chapter', seq.name);
                if (name?.trim()) edit('Rename chapter', (p) => renameSequence(p, seq.id, name.trim()));
              }}
            >
              Rename
            </button>
            {!isRoot && (
              <button
                className="btn ghost small icon"
                {...tip('Delete this chapter (only possible when it is not used on a timeline)')}
                onClick={() => edit('Delete chapter', (p) => deleteSequence(p, seq.id))}
              >
                <Trash2 size={12} />
              </button>
            )}
          </div>
        );
      })}
      <button
        className="btn chapter-add"
        {...tip('Create an empty chapter and open it')}
        onClick={() => {
          const n = Object.keys(project.sequences).length;
          const id = edit('New chapter', (p) => addSequence(p, `Chapter ${n}`));
          if (id) openSequence(id);
        }}
      >
        <Plus size={14} /> New chapter
      </button>
    </div>
  );
}

function EffectsTab() {
  return (
    <div className="effects">
      <div className="effects-section">Generators</div>
      <div className="effects-grid">
        <button
          className="effect-tile"
          onClick={addTextClip}
          {...commandTip('text', 'Add a text clip at the playhead')}
        >
          <Type size={18} /> Text
        </button>
        <button
          className="effect-tile"
          onClick={addSolidClip}
          {...tip('Add a full-frame color clip at the playhead')}
        >
          <Palette size={18} /> Color solid
        </button>
      </div>
      <div className="effects-section">Transitions</div>
      <div className="muted effects-hint">Drag onto a cut between two clips.</div>
      <div className="effects-grid">
        {(Object.keys(TRANSITION_LABELS) as TransitionType[]).map((t) => (
          <div
            key={t}
            className={`effect-tile transition-tile t-${t}`}
            draggable
            {...tip(`${TRANSITION_DESCRIPTIONS[t]}. Drag onto a cut between two touching clips.`)}
            onDragStart={(e) => {
              e.dataTransfer.setData(DND_TRANSITION, t);
              e.dataTransfer.effectAllowed = 'copy';
            }}
          >
            <span className="transition-swatch" />
            {TRANSITION_LABELS[t]}
          </div>
        ))}
      </div>
    </div>
  );
}
